import {
  Box, Button, Divider, HStack, Link, Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  VStack
} from "@chakra-ui/react";
import React from "react";
import { AiOutlineInfoCircle } from "react-icons/ai";
import styled from "styled-components";
import { InclusionAndOverage, UserSessionResponse } from "../../../interfaces";


export const Plans = ({ user, onOpenPlanUpgrade }: {
  user: UserSessionResponse,
  onOpenPlanUpgrade: () => void
}): React.ReactElement => {
  const DAM_ONLY_PLAN = user?.pricingPlan?.planType === "DAM_ONLY";

  const NORMAL_PLAN_ENTERPRISE_FEATURE_DISPLAY_LIST = [
    "Advanced Security with IP, geo, and referrer-based allow or block lists",
    "Origin shield for external origins",
    "Custom CDN integration",
    "Dedicated customer success manager",
    "Dedicated integration support",
    "Custom processing limits",
    "Phone and Slack support with custom SLAs"
  ];

  const DAM_ONLY_PLAN_ENTERPRISE_FEATURE_DISPLAY_LIST_WITHOUT_DAM = [
    "SSO",
    "Dedicated customer success manager",
    "Managed migration",
    "Custom processing limits",
    "Audit logs"
  ];

  const ENTERPRISE_FEATURES = DAM_ONLY_PLAN ? DAM_ONLY_PLAN_ENTERPRISE_FEATURE_DISPLAY_LIST_WITHOUT_DAM : NORMAL_PLAN_ENTERPRISE_FEATURE_DISPLAY_LIST;

  const pricingPageLink = DAM_ONLY_PLAN ? "https://imagekit.io/plans/?tab=dam" : "https://imagekit.io/plans/";

  const freePlan = user.currentPricingPlanForUpgradePageDataStructure || (DAM_ONLY_PLAN ? user.damFreePlan : user.freePlan)
  const paidPlan = DAM_ONLY_PLAN ? user.damPremiumPlan : user.premiumPlan

  return (
    <HStack alignItems={"flex-start"} spacing={6}>
      <VStack border="1px solid #E2E8F0" padding={"32px 16px"} borderRadius={"8px"} maxW="368px" align="left">
        <Box>
          {freePlan.planName}
        </Box>
        <Box fontSize={20}>
          <HStack spacing={2}>
            <Box fontWeight="semibold">
              ${freePlan.inclusionAndOverage.fixedCost}
            </Box>
            <Box fontSize={12}>
              monthly
            </Box>
          </HStack>
        </Box>
        <Box color={"appSlate"} height={"70px"}>
          always
        </Box>
        <Box style={{ marginTop: "24px" }}>
          <Button isFullWidth={true} variant="outline" disabled={true}>Current plan</Button>
        </Box>
        <Box color={"appSlate"} style={{ margin: "24px 0" }}>
          Inclusions (hard limit, no overage)
        </Box>
        <Box>
          <PlanBasicDetails user={user} inclusionAndOverage={freePlan.inclusionAndOverage} isPaidPlan={false}></PlanBasicDetails>
        </Box>
        <Divider borderColor="appPaleBlue" style={{ margin: "24px 0" }} />
        <Box color={"appSlate"} style={{ marginBottom: "24px" }}>
          Free includes:
        </Box>
        <VStack spacing={6} align="left">
          {freePlan.planDetails.map((detail, index) => {
            if (detail.text) {
              return (
                <PlanBasicDetailsItem key={index}>
                  {detail.text}
                </PlanBasicDetailsItem>
              )
            }
          })}
        </VStack>
      </VStack>

      <VStack border="2px solid" borderColor={"appBlue"} padding={"24px 16px"} borderRadius={"8px"} maxW="368px" align="left" position="relative">
        <MostPopular>
          Recommended
        </MostPopular>
        <Box>
          {paidPlan.planName}
        </Box>
        <Box fontSize={20}>
          <HStack spacing={2}>
            <Box fontWeight="semibold">
              ${paidPlan.inclusionAndOverage.fixedCost}
            </Box>
            <Box fontSize={12}>
              monthly
            </Box>
          </HStack>
        </Box>
        <Box color={"appSlate"} height={"70px"}>
          + pay-as-you-go
        </Box>
        <Box style={{ marginTop: "24px" }}>
          <Button isFullWidth={true} onClick={onOpenPlanUpgrade}>Upgrade plan</Button>
        </Box>
        <Box color={"appSlate"} style={{ margin: "24px 0" }}>
          Inclusions with <Link href={pricingPageLink} target="_blank">pay-per-use pricing after that</Link>:
        </Box>
        <Box>
          <PlanBasicDetails user={user} inclusionAndOverage={paidPlan.inclusionAndOverage} isPaidPlan={true}></PlanBasicDetails>
        </Box>
        <Divider borderColor="appPaleBlue" style={{ margin: "24px 0" }} />
        <Box color={"appSlate"} style={{ marginBottom: "24px" }}>
          Everything in free plan, and
        </Box>
        <VStack spacing={6} align="left">
          {paidPlan.planDetails.map((detail, index) => {
            if (detail.text) {
              return (
                <PlanBasicDetailsItem key={index}>
                  {detail.text}
                </PlanBasicDetailsItem>
              )
            }
          })}
        </VStack>
      </VStack>

      <VStack border="1px solid #E2E8F0" padding={"32px 16px"} borderRadius={"8px"} maxW="368px" align="left">
        <Box>
          Custom plan
        </Box>
        <Box fontSize={20}>
          <HStack spacing={2}>
            <Box fontWeight="semibold">
              Enterprise Plan
            </Box>
          </HStack>
        </Box>
        <Box color={"appSlate"} height={"70px"}>
          Expecting to spend over $500 with pay-as-you-go? Contact us for discounted pricing.
        </Box>

        <Box style={{ marginTop: "24px" }}>
          <Button isFullWidth={true} as={"a"} variant="outline" href="https://imagekit.io/contact-us/" target="_blank">Contact for custom pricing</Button>
        </Box>
        <Box color={"appSlate"} style={{ margin: "24px 0" }}>
          Everything in premium, and
        </Box>
        <VStack spacing={6} align="left">
          {ENTERPRISE_FEATURES.map((detail, index) => {
            return (
              <PlanBasicDetailsItem key={index}>
                {detail}
              </PlanBasicDetailsItem>
            )
          })}
        </VStack>
      </VStack>
    </HStack>
  )
}

const PlanBasicDetails = ({ user, inclusionAndOverage, isPaidPlan }: {
  user: UserSessionResponse,
  inclusionAndOverage: InclusionAndOverage,
  isPaidPlan: boolean
}) => {
  const DAM_ONLY_PLAN = user?.pricingPlan?.planType === "DAM_ONLY";
  return (
    <VStack spacing={6} align="left">
      {!DAM_ONLY_PLAN &&
        <PlanBasicDetailsItem>
          <b>{inclusionAndOverage.bandwidth.inclusion}GB</b> media delivery bandwidth
        </PlanBasicDetailsItem>
      }
      <PlanBasicDetailsItem>
        <b>{inclusionAndOverage.storage.inclusion}GB</b> DAM storage
      </PlanBasicDetailsItem>
      {!DAM_ONLY_PLAN &&
        <>
          <PlanBasicDetailsItem>
            <HStack>
              <Box>
                <b>{inclusionAndOverage.video.inclusion}</b> video processing units&nbsp;
              </Box>
              <VideoProcessingUnitsHelp></VideoProcessingUnitsHelp>
            </HStack>
          </PlanBasicDetailsItem>

          <PlanBasicDetailsItem>
            <HStack>
              <Box>
                <b>{inclusionAndOverage.extension.inclusion}</b> extension units&nbsp;
              </Box>
              <ExtensionUnitsHelp></ExtensionUnitsHelp>
            </HStack>
          </PlanBasicDetailsItem>
        </>
      }
      <PlanBasicDetailsItem>
        <b>{inclusionAndOverage.userPrice.inclusion}</b> users&nbsp;
      </PlanBasicDetailsItem>
      {!DAM_ONLY_PLAN &&
        <>
          <PlanBasicDetailsItem>
            <b>{inclusionAndOverage.purgeCacheAPIPrice.inclusion}</b> purge cache requests&nbsp;
          </PlanBasicDetailsItem>
          <PlanBasicDetailsItem>
            <b>{inclusionAndOverage.CNAMEPrice.inclusion}</b> custom domain names&nbsp;
          </PlanBasicDetailsItem>
        </>
      }
    </VStack>
  )
}

const PlanBasicDetailsItem = ({ children }) => {
  return (
    <HStack>
      <img src="https://ik.imagekit.io/ikmedia/round-checkmark.svg" />
      <Box position={"relative"}>
        {children}
      </Box>
    </HStack>
  )
}


export const VideoProcessingUnitsHelp = () => {
  return (
    <Popover trigger="hover">
      <PopoverTrigger>
        <AiOutlineInfoCircle color="#888" style={{ cursor: "help" }} size={18}></AiOutlineInfoCircle>
      </PopoverTrigger>
      <PopoverContent width={400}>
        <PopoverBody>
          <p>
            Video processing units used depends on output video duration and resolution:
          </p>
          <br></br>
          <p>
            <ul style={{ paddingLeft: 20, listStyle: "initial" }}>
              <li>1 second of SD video output = 1 unit</li>
              <li>1 second of HD video output = 2 units</li>
              <li>1 second of 4K video output = 4 units</li>
              <li>1 second of 8K video output = 8 units</li>
              <li>1 second of 16K video output = 16 units</li>
            </ul>
          </p>
          <br></br>
          <p>
            Every new video transformation that has never been done before will contribute towards video processing units using the above definition. Subsequent views of the same video transformation only count towards bandwidth. Refer to the <Link href="https://docs.imagekit.io/features/video-transformation#pricing" target="_blank">documentation</Link> for the definition of resolution.
          </p>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  )
}

export const ExtensionUnitsHelp = () => {

  return (
    <Popover trigger="hover">
      <PopoverTrigger>
        <AiOutlineInfoCircle color="#888" style={{ cursor: "help" }} size={18}></AiOutlineInfoCircle>
      </PopoverTrigger>
      <PopoverContent width={400}>
        <PopoverBody>
          <p>
            The extension allows you to perform advanced operations e.g., background removal and auto-tagging, using third-party services. Extension unit consumption depends on which extension is being executed. Refer to the <Link href="https://docs.imagekit.io/extensions/overview#extension-pricing" target="_blank">documentation</Link> for the latest pricing information.
          </p>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  )
}

export const MostPopular = styled.div`
    position: absolute;
    transform: translateY(-122%) translateX(43%);
    width: 50%;
    color: #fff;
    padding: 8px 0;
    border-radius: 44px;
    background-color: #0450d5;
    text-align: center;
`;